// TicketTemplatesTab.jsx
import React, { useMemo, useState } from "react";
import {
  Button,
  Input,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "@/design-system/native";
import { Edit, MoreVertical, Search as SearchIcon, Columns2, X } from "lucide-react";

import TableCard from "../../Tables/DataTable/TableCard";
import DataTable from "../../Tables/DataTable/DataTable";
import ImportButton from "../../Common/ImportButton";
import ExportButtons from "../../Common/ExportButtons";
import styles from "./TicketTemplatesTab.module.scss";

const TicketTemplatesTab = ({
  templates = [],
  setTemplates,
  onRequestEdit,
  onDelete,
  onAddTemplate, // üstteki + Add template butonunu açtırmak için (TicketPage'de kullanılıyor)
}) => {
  const templateImportFields = [
    { label: "Title", name: "title" },
    { label: "Description", name: "description" },
    { label: "Category", name: "type" },
    { label: "Private", name: "private" },
  ];
  // kolon görünürlüğü
  const [colMenuOpen, setColMenuOpen] = useState(false);
  const [hiddenCols, setHiddenCols] = useState(() => new Set());
  const toggleCol = (id) =>
    setHiddenCols((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // arama
  const [q, setQ] = useState("");

  // kolonlar
  const columns = useMemo(
    () => [
      { id: "title", header: "Title", accessor: "title", width: 260 },
      {
        id: "description",
        header: "Description",
        accessor: "description",
        width: 360,
      },
      { id: "type", header: "Category", accessor: "type", width: 160 },
      {
        id: "private",
        header: "Private",
        width: 100,
        cell: (row) => (row.private ? "Yes" : "No"),
      },
      {
        id: "actions",
        header: <MoreVertical size={16} />,
        width: 120,
        cell: (row) => (
          <div className={styles.actions}>
            <Button
              size="xs"
              color="light"
              onClick={() => onRequestEdit?.(row)}
              className={styles.actionBtn}
            >
              <Edit color="#10161b" size={18} />
            </Button>
            <Button
              size="xs"
              color="light"
              onClick={() => onDelete?.(row.id)}
              className={styles.actionBtn}
            >
              <X color="#10161b" size={18} />
            </Button>
          </div>
        ),
      },
    ],
    [onRequestEdit, onDelete]
  );

  // filtrelenmiş satırlar (search)
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return templates;
    return templates.filter((t) =>
      [t.title, t.description, t.type, t.private ? "yes" : "no"]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s))
    );
  }, [q, templates]);

  // header slotları
  const leftSlot = (
    <Dropdown isOpen={colMenuOpen} toggle={() => setColMenuOpen((v) => !v)}>
      <DropdownToggle color="white" className={styles.colToggle}>
        <Columns2 size={16} />
      </DropdownToggle>
      <DropdownMenu className={styles.colMenu}>
        {columns.map((col) => (
          <DropdownItem
            key={col.id}
            toggle={false}
            onClick={() => toggleCol(col.id)}
          >
            <input
              type="checkbox"
              readOnly
              checked={!hiddenCols.has(col.id)}
              className={styles.colCheckbox}
            />
            {col.header || "(untitled)"}
          </DropdownItem>
        ))}
      </DropdownMenu>
    </Dropdown>
  );

  const rightSlot = (
    <div className={styles.rightSlot}>
      <div className={styles.toolGroup}>
        <ImportButton
          title="Import templates"
          label="Import templates"
          fields={templateImportFields}
          setRows={setTemplates}
        />
        <ExportButtons
          rows={filtered}
          columns={columns}
          fileName="ticket_templates"
          sheetName="Templates"
          title="Ticket Templates"
        />
      </div>
      <div className={styles.search}>
        <Input
          type="search"
          placeholder="Search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className={styles.searchInput}
        />
        <SearchIcon size={16} className={styles.searchIcon} />
      </div>
    </div>
  );

  return (
    <TableCard title="Templates" leftSlot={leftSlot} rightSlot={rightSlot}>
      <div className={styles.body}>
        <DataTable
          columns={columns}
          rows={filtered}
          hiddenColumnIds={[...hiddenCols]}
          sortBy={null}
          sortDir={"asc"}
          onSort={() => {}}
        />
      </div>

      <div className={styles.pager}>
        {filtered.length === 0
          ? "0–0 / 0"
          : `1–${filtered.length} / ${filtered.length}`}
      </div>
    </TableCard>
  );
};

export default TicketTemplatesTab;
